/**
 * PATH: packages/email-hub/src/feature/components/email-allocation/useWatchers.ts
 * FILE_ID: EMAILHUB-ALLOCATION-WATCHER-001-HOOK
 * PURPOSE: Owns watcher candidates and invite/remove mutations for the rail.
 */
import { useCallback, useMemo } from 'react'

import type { EmailWatcher } from '../../hooks/useEmails'
import type {
  AllocationMember,
  WatchersHookInput,
  WatchersViewModel,
} from './types'

export function useWatchers({
  emailId,
  watchers,
  members,
  assigneeUserId,
  busyKey,
  setBusyKey,
  showPicker,
  setShowPicker,
  inviteEmailWatcher,
  removeEmailWatcher,
  onWatchersChanged,
  toast,
}: WatchersHookInput): WatchersViewModel {
  const watcherList = useMemo(() => watchers ?? [], [watchers])
  const watcherUserIds = useMemo(
    () => new Set(watcherList.map((watcher) => watcher.user_id)),
    [watcherList],
  )

  const inviteWatcher = useCallback(
    async (member: AllocationMember) => {
      if (busyKey) return
      setBusyKey(`watcher-add:${member.id}`)
      setShowPicker(false)
      try {
        const watcher = await inviteEmailWatcher(emailId, member.id)
        const next = [
          ...watcherList.filter((current) => current.user_id !== member.id),
          watcher,
        ]
        onWatchersChanged?.(next)
        toast(`Invited ${member.name}`, 'success')
      } catch (err) {
        toast(
          `Could not invite: ${(err as Error).message || 'unknown error'}`,
          'error',
        )
      } finally {
        setBusyKey(null)
      }
    },
    [
      busyKey,
      emailId,
      inviteEmailWatcher,
      watcherList,
      onWatchersChanged,
      toast,
      setBusyKey,
      setShowPicker,
    ],
  )

  const removeWatcher = useCallback(
    async (watcher: EmailWatcher) => {
      if (busyKey) return
      setBusyKey(`watcher-remove:${watcher.user_id}`)
      try {
        await removeEmailWatcher(emailId, watcher.user_id)
        const next = watcherList.filter((current) => current.user_id !== watcher.user_id)
        onWatchersChanged?.(next)
        toast(`Removed ${watcher.user_name || 'watcher'}`, 'success')
      } catch (err) {
        toast(
          `Could not remove: ${(err as Error).message || 'unknown error'}`,
          'error',
        )
      } finally {
        setBusyKey(null)
      }
    },
    [
      busyKey,
      emailId,
      removeEmailWatcher,
      watcherList,
      onWatchersChanged,
      toast,
      setBusyKey,
    ],
  )

  // Members available for invite (exclude already-watching + the
  // current assignee — adding the assignee as a watcher is a
  // double-up; the assignee is already the primary recipient).
  const inviteCandidates = useMemo(
    () =>
      members.filter(
        (m) => !watcherUserIds.has(m.id) && m.id !== assigneeUserId,
      ),
    [members, watcherUserIds, assigneeUserId],
  )

  return {
    members,
    watcherList,
    inviteCandidates,
    busyKey,
    showPicker,
    setShowPicker,
    inviteWatcher,
    removeWatcher,
  }
}
